import { useSelector } from 'react-redux'
import { Route, useHistory, useRouteMatch } from 'react-router-dom'

// eslint-disable-next-line @nx/enforce-module-boundaries
import MembershipWizardModal from '@sweetspot/club-portal-legacy/modals/MembershipWizardModal'
import { Button, Popover } from '@sweetspot/scramble-ds/atoms'
import {
  DataTable,
  DataTableFilters,
  Input,
  InputBase,
  InputContainer,
  InputLeadingContainer,
  Section,
  SectionContent,
  SectionHeader,
  SectionTitle,
} from '@sweetspot/scramble-ds'
import { useTranslation } from 'react-i18next'
import { columns } from './columns'
// eslint-disable-next-line @nx/enforce-module-boundaries
import { RootState } from '@sweetspot/club-portal-legacy/store/types'
import { useSubscriptionTable } from '../hooks/useSubscriptionTable'
import { SubscriptionType } from '@sweetspot/shared/types'
import { filters } from '../filters'
import { cn } from '@sweetspot/scramble-ds/utils'
import {
  getFirstMatchingRole,
  hasAccess,
} from '@sweetspot/sweetspot-js/features/userAccess/utils/utils'
import { ACCESS_KEYS } from '@sweetspot/sweetspot-js/features/userAccess/constants/accessTable'
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  PlayValueImportSheet,
  PlayerImportSheet,
} from '@sweetspot/club-portal/feature/player-imports'

export const PassesTable = () => {
  const role = useSelector((state: RootState) => getFirstMatchingRole(state.auth.roles))

  const { t } = useTranslation()
  const history = useHistory()
  const { path } = useRouteMatch()

  const {
    subscriptions: passes,
    activeFilters,
    sortItems,
    currentPage,
    pageSize,
    searchString,
    isLoading,
    setSortItems,
    totalItems,
    setCurrentPage,
    setPageSize,
    handleSearchChange,
    onFilterChange,
    onRowMoveHandler,
  } = useSubscriptionTable({ subscriptionType: SubscriptionType.PASS })

  const createNewMembership = () => {
    history.push('/passes/new')
  }

  const handleRowClick = (membershipId: string | number) => {
    history.push(`/passes/${membershipId}`)
  }

  const activePeriodPasses = passes?.filter(
    (pass) => pass?.duration_original?.includes('period') && pass?.status?.state === 'active'
  )

  return (
    <Section className="overflow-x-hidden">
      <SectionHeader>
        <SectionTitle>{t('words.pass_other')}</SectionTitle>
      </SectionHeader>
      <SectionContent>
        <DataTable
          columns={columns(t, sortItems, setSortItems)}
          data={passes}
          onRowClick={handleRowClick}
          enableRowSelection={false}
          onRowMove={onRowMoveHandler}
          sortItems={sortItems}
          inputSearch={() => (
            <div className="flex w-2/5 flex-row">
              <InputBase className="flex w-full flex-row gap-2 px-4 py-6">
                <InputContainer>
                  <InputLeadingContainer>
                    <i className="fa-regular fa-magnifying-glass" />
                  </InputLeadingContainer>
                  <Input
                    rounding="pill"
                    className="h-10 px-10"
                    placeholder={t('membership.search_pass')}
                    value={searchString}
                    onChange={handleSearchChange}
                  />
                </InputContainer>
              </InputBase>
              <div className="flex w-[20px] items-center justify-center ">
                <i
                  className={cn(
                    'fa-regular fa-loader animate-[spin_3s_linear_infinite] text-xl opacity-0',
                    {
                      'opacity-100': isLoading,
                    }
                  )}
                />
              </div>
            </div>
          )}
          extraControls={(table) => (
            <>
              <Button
                size="small"
                variant="clear-dark"
                rounding="pill"
                onClick={createNewMembership}
                className="hover:!text-text-dark hover:bg-transparent hover:shadow-none focus:!outline-none"
              >
                <i className="fa-regular fa-square-plus text-lg" />
              </Button>
              <DataTableFilters
                table={table}
                filters={filters}
                onFilterChange={onFilterChange}
                activeFilters={activeFilters}
              />

              {hasAccess(ACCESS_KEYS.PAGES.MEMBERSHIPS.IMPORT_INVITES, role?.value) && (
                <Popover.Root>
                  <Popover.Trigger asChild>
                    <div className="cursor-pointer px-4">
                      <i className="fa-regular fa-ellipsis text-base" />
                    </div>
                  </Popover.Trigger>
                  <Popover.Content className="w-[300px] p-2 drop-shadow" align="end" side="bottom">
                    {activePeriodPasses.length > 0 && (
                      <PlayValueImportSheet passes={activePeriodPasses} isLoading={isLoading} />
                    )}
                    <PlayerImportSheet subscriptionType={SubscriptionType.PASS} />
                    <Popover.Arrow className="fill-white" width={20} height={10} />
                  </Popover.Content>
                </Popover.Root>
              )}
            </>
          )}
          pagination={{
            currentPage,
            pageSize,
            totalRows: totalItems,
            onPageChange: (pageIndex: number) => setCurrentPage(pageIndex),
            onPageSizeChange: (size: number) => {
              setCurrentPage(0)
              setPageSize(size)
            },
          }}
        />
      </SectionContent>
      <Route
        path={`${path}/:membershipId`}
        render={(props) => (
          <MembershipWizardModal {...props} subscriptionType={SubscriptionType.PASS} />
        )}
      />
    </Section>
  )
}
